// frontend/components/HrManagerEmployeesTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchEmployeesList,
  updateEmployeeProfile,
  createEmployee,
  deleteEmployeeProfile,
} from "../features/hr/hrThunks.js";
import {
  Search,
  UserCircle2,
  Plus,
  X,
  Trash2,
  Pencil,
  Briefcase,
} from "lucide-react";
import "../styles/hr/hr-manager-employees.css";

const getEmploymentTypeLabel = (type) => {
  if (type === "full_time") return "Jornada completa";
  if (type === "part_time") return "Media jornada";
  if (type === "contractor") return "Colaborador externo";
  if (type === "intern") return "Prácticas";
  return type || "-";
};

const getStatusLabel = (status) => {
  if (status === "active") return "Activo";
  if (status === "on_leave") return "En baja / permiso";
  if (status === "inactive") return "Inactivo";
  return status || "-";
};

const getRoleLabel = (role) => {
  if (role === "admin") return "Administrador";
  if (role === "doctor") return "Médico";
  if (role === "receptionist") return "Recepcionista";
  return role || "-";
};

const getErrorText = (errorValue) => {
  if (!errorValue) return null;
  if (typeof errorValue === "string") return errorValue;
  return errorValue?.detail || "Ha ocurrido un error.";
};

const HrManagerEmployeesTab = () => {
  const dispatch = useDispatch();

  const employeesListRaw = useSelector((state) => state.hr.employeesList) || [];
  const loadingEmployeesList = useSelector((state) => state.hr.loadingEmployeesList);
  const errorEmployeesList = useSelector((state) => state.hr.errorEmployeesList);

  const loadingDeleteEmployeeProfile = useSelector(
    (state) => state.hr.loadingDeleteEmployeeProfile
  );
  const errorDeleteEmployeeProfile = useSelector(
    (state) => state.hr.errorDeleteEmployeeProfile
  );

  const loadingUpdateEmployeeProfile = useSelector(
    (state) => state.hr.loadingUpdateEmployeeProfile
  );
  const errorUpdateEmployeeProfile = useSelector(
    (state) => state.hr.errorUpdateEmployeeProfile
  );

  const loadingCreateEmployee = useSelector((state) => state.hr.loadingCreateEmployee);
  const errorCreateEmployee = useSelector((state) => state.hr.errorCreateEmployee);

  const [searchQuery, setSearchQuery] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteSuccessMessage, setDeleteSuccessMessage] = useState("");

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    position: "",
    department: "",
    employment_type: "",
    weekly_hours: "",
    start_date: "",
    end_date: "",
    status: "",
    annual_vacation_days: "",
  });
  const [saveSuccessMessage, setSaveSuccessMessage] = useState("");

  // ---- Crear empleado (modal) ----
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    role: "receptionist",
    password: "",

    position: "",
    department: "",
    employmentType: "full_time",
    status: "active",
    weeklyHours: "40",
    annualVacationDays: "22",
    startDate: "",
    endDate: "",
    managerId: "",
  });
  const [createLocalError, setCreateLocalError] = useState("");
  const [createSuccessMessage, setCreateSuccessMessage] = useState("");

  useEffect(() => {
    dispatch(fetchEmployeesList());
  }, [dispatch]);

  // Soportar tanto array directo como objeto paginado { results: [...] }
  const employeesList = useMemo(() => {
    if (Array.isArray(employeesListRaw)) return employeesListRaw;
    if (employeesListRaw && Array.isArray(employeesListRaw.results)) {
      return employeesListRaw.results;
    }
    return [];
  }, [employeesListRaw]);

  // Opciones de filtros (departamentos / estados)
  const departmentOptions = useMemo(() => {
    const set = new Set();
    employeesList.forEach((employee) => {
      if (employee.department) set.add(employee.department);
    });
    return Array.from(set).sort();
  }, [employeesList]);

  const statusOptions = useMemo(() => {
    const set = new Set();
    employeesList.forEach((employee) => {
      if (employee.status) set.add(employee.status);
    });
    return Array.from(set);
  }, [employeesList]);

  // Filtro + búsqueda
  const filteredEmployees = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return employeesList.filter((employee) => {
      const user = employee.user || {};
      const fullName = ((user.first_name || "") + " " + (user.last_name || "")).trim();
      const username = user.username || "";
      const email = user.email || "";

      if (departmentFilter && employee.department !== departmentFilter) return false;
      if (statusFilter && employee.status !== statusFilter) return false;
      if (!query) return true;

      const haystack = (
        fullName +
        " " +
        username +
        " " +
        email +
        " " +
        (employee.position || "") +
        " " +
        (employee.department || "")
      ).toLowerCase();

      return haystack.includes(query);
    });
  }, [employeesList, searchQuery, departmentFilter, statusFilter]);

  // Seleccionar por defecto el primero al cargar
  useEffect(() => {
    if (!selectedEmployeeId && filteredEmployees.length > 0) {
      setSelectedEmployeeId(filteredEmployees[0].id);
    }
  }, [filteredEmployees, selectedEmployeeId]);

  // Si el seleccionado desaparece por filtros/refresh, seleccionar el primero
  useEffect(() => {
    if (!selectedEmployeeId) return;
    const exists = filteredEmployees.some((e) => e.id === selectedEmployeeId);
    if (!exists) {
      setSelectedEmployeeId(filteredEmployees[0]?.id || null);
    }
  }, [filteredEmployees, selectedEmployeeId]);

  const selectedEmployee =
    filteredEmployees.find((e) => e.id === selectedEmployeeId) ||
    filteredEmployees[0] ||
    null;

  const selectedEmployeeUserForDelete = selectedEmployee?.user || {};
  const selectedEmployeeNameForDelete =
    ((selectedEmployeeUserForDelete.first_name || "") +
      " " +
      (selectedEmployeeUserForDelete.last_name || "")).trim() ||
    selectedEmployeeUserForDelete.username ||
    selectedEmployeeUserForDelete.email ||
    "este empleado";

  const handleClearFilters = () => {
    setSearchQuery("");
    setDepartmentFilter("");
    setStatusFilter("");
  };

  const handleEditFieldChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const fillEditFormFromEmployee = (employee) => {
    if (!employee) return;

    setEditForm({
      position: employee.position || "",
      department: employee.department || "",
      employment_type: employee.employment_type || "",
      weekly_hours:
        employee.weekly_hours !== null && employee.weekly_hours !== undefined
          ? String(employee.weekly_hours)
          : "",
      start_date: employee.start_date || "",
      end_date: employee.end_date || "",
      status: employee.status || "",
      annual_vacation_days:
        employee.annual_vacation_days !== null &&
        employee.annual_vacation_days !== undefined
          ? String(employee.annual_vacation_days)
          : "",
    });
  };

  const handleStartEditing = () => {
    if (!selectedEmployee) return;
    setCreateSuccessMessage("");
    setSaveSuccessMessage("");
    fillEditFormFromEmployee(selectedEmployee);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    fillEditFormFromEmployee(selectedEmployee);
    setIsEditing(false);
    setSaveSuccessMessage("");
  };

  // Cuando cambia el empleado seleccionado, reset de edición y rellenar form
  useEffect(() => {
    setSaveSuccessMessage("");
    if (!selectedEmployee) {
      setIsEditing(false);
      return;
    }
    setIsEditing(false);
    fillEditFormFromEmployee(selectedEmployee);
  }, [selectedEmployee]);

  const handleSave = async () => {
    if (!selectedEmployeeId) return;
    setSaveSuccessMessage("");

    const payload = {
      position: editForm.position || "",
      department: editForm.department || "",
      employment_type: editForm.employment_type || "",
      status: editForm.status || "",
      weekly_hours: editForm.weekly_hours !== "" ? Number(editForm.weekly_hours) : null,
      annual_vacation_days:
        editForm.annual_vacation_days !== ""
          ? Number(editForm.annual_vacation_days)
          : null,
      start_date: editForm.start_date || null,
      end_date: editForm.end_date || null,
    };

    try {
      await dispatch(
        updateEmployeeProfile({ employeeId: selectedEmployeeId, payload })
      ).unwrap();

      setIsEditing(false);
      setSaveSuccessMessage("Datos actualizados correctamente.");
      dispatch(fetchEmployeesList());
    } catch (error) {
      console.error("Error al actualizar empleado:", error);
    }
  };

  // --- Helpers manager name robusto (id u objeto) ---
  const getManagerNameForEmployee = (employee) => {
    if (!employee || !employee.manager) return null;

    if (typeof employee.manager === "object") {
      const managerUser = employee.manager.user || employee.manager;
      const managerFullName =
        ((managerUser.first_name || "") + " " + (managerUser.last_name || "")).trim();
      return managerFullName || managerUser.username || null;
    }

    const managerUserId = employee.manager;
    const managerEmployeeProfile = employeesList.find((e) => e.user?.id === managerUserId);
    const managerUser = managerEmployeeProfile?.user || null;
    if (!managerUser) return null;

    const managerFullName =
      ((managerUser.first_name || "") + " " + (managerUser.last_name || "")).trim();
    return managerFullName || managerUser.username || null;
  };

  // ---- Delete modal ----
  const openDeleteModal = () => {
    setIsEditing(false);
    setSaveSuccessMessage("");
    setCreateSuccessMessage("");
    setDeleteSuccessMessage("");
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (loadingDeleteEmployeeProfile) return;
    setIsDeleteModalOpen(false);
  };

  useEffect(() => {
    if (!isDeleteModalOpen) return;

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (loadingDeleteEmployeeProfile) return;
      closeDeleteModal();
    };

    document.addEventListener("keydown", handleKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isDeleteModalOpen, loadingDeleteEmployeeProfile]);

  const handleConfirmDelete = async () => {
    if (!selectedEmployeeId) return;

    try {
      await dispatch(deleteEmployeeProfile(selectedEmployeeId)).unwrap();
      setIsDeleteModalOpen(false);
      setSelectedEmployeeId(null);
      setDeleteSuccessMessage("Empleado eliminado correctamente.");
      dispatch(fetchEmployeesList());
    } catch (error) {
      console.error("Error al eliminar empleado:", error);
    }
  };

  // ---- Modal crear empleado ----
  const openCreateModal = () => {
    setDeleteSuccessMessage("");
    setSaveSuccessMessage("");
    setCreateSuccessMessage("");
    setCreateLocalError("");
    setIsEditing(false);
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setCreateLocalError("");
    setCreateForm({
      email: "",
      firstName: "",
      lastName: "",
      phone: "",
      role: "receptionist",
      password: "",

      position: "",
      department: "",
      employmentType: "full_time",
      status: "active",
      weeklyHours: "40",
      annualVacationDays: "22",
      startDate: "",
      endDate: "",
      managerId: "",
    });
  };

  useEffect(() => {
    if (!isCreateModalOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeCreateModal();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isCreateModalOpen]);

  const handleCreateFieldChange = (field, value) => {
    setCreateForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateEmployee = async () => {
    setCreateSuccessMessage("");
    setCreateLocalError("");

    const email = (createForm.email || "").trim();
    if (!email) {
      setCreateLocalError("El email es obligatorio.");
      return;
    }
    if (!createForm.role) {
      setCreateLocalError("El rol es obligatorio.");
      return;
    }

    const managerId =
      createForm.managerId && String(createForm.managerId).trim()
        ? Number(createForm.managerId)
        : null;

    try {
      const createdEmployeeProfile = await dispatch(
        createEmployee({
          email: email,
          firstName: createForm.firstName,
          lastName: createForm.lastName,
          phone: createForm.phone,
          role: createForm.role,
          password: createForm.password,

          position: createForm.position,
          department: createForm.department,
          employmentType: createForm.employmentType,
          status: createForm.status,
          weeklyHours: createForm.weeklyHours,
          annualVacationDays: createForm.annualVacationDays,
          startDate: createForm.startDate || null,
          endDate: createForm.endDate || null,
          managerId: managerId,
        })
      ).unwrap();

      closeCreateModal();
      dispatch(fetchEmployeesList());

      if (createdEmployeeProfile?.id) {
        setDepartmentFilter("");
        setStatusFilter("");
        setSearchQuery("");
        setSelectedEmployeeId(createdEmployeeProfile.id);
      }

      setCreateSuccessMessage("Empleado creado correctamente.");
    } catch (error) {
      console.error("Error al crear empleado:", error);
      if (typeof error === "string") setCreateLocalError(error);
    }
  };

  // Opciones de responsable (usuarios existentes)
  const managerOptions = useMemo(() => {
    return employeesList
      .map((employee) => {
        const user = employee.user || {};
        const fullName = ((user.first_name || "") + " " + (user.last_name || "")).trim();
        const label = fullName || user.username || user.email || "Sin nombre";
        return { userId: user.id, label };
      })
      .filter((item) => item.userId)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [employeesList]);

  const errorEmployeesText = getErrorText(errorEmployeesList);
  const errorUpdateText = getErrorText(errorUpdateEmployeeProfile);
  const errorCreateText = getErrorText(errorCreateEmployee);
  const errorDeleteText = getErrorText(errorDeleteEmployeeProfile);

  return (
    <div className="hr-manager-tab hr-manager-team-tab">
      <div className="hr-manager-team-header">
        <div>
          <h2 className="hr-manager-team-title">Equipo</h2>
          <p className="hr-tab-helper-text">
            Consulta la lista de empleados, filtra por departamento o estado y accede a su ficha rápida.
          </p>

          {createSuccessMessage && (
            <div className="hr-success-text" style={{ marginTop: 10 }}>
              {createSuccessMessage}
            </div>
          )}
        </div>

        <div className="hr-manager-team-count">
          <span className="hr-pill">{employeesList.length} empleados</span>
          <button
            type="button"
            className="hr-button hr-button--primary hr-button--with-icon"
            onClick={openCreateModal}
          >
            <Plus aria-hidden="true" size={18} />
            Crear empleado
          </button>
        </div>
      </div>

      {/* Barra de filtros */}
      <div className="hr-manager-team-filters">
        <div className="hr-filter-search">
          <Search className="hr-filter-search-icon" aria-hidden="true" />
          <input
            type="text"
            placeholder="Buscar por nombre, email, puesto..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </div>

        <div className="hr-filter-selects">
          <div className="hr-filter-select">
            <label>Departamento</label>
            <select
              value={departmentFilter}
              onChange={(event) => setDepartmentFilter(event.target.value)}
            >
              <option value="">Todos</option>
              {departmentOptions.map((dep) => (
                <option key={dep} value={dep}>
                  {dep}
                </option>
              ))}
            </select>
          </div>

          <div className="hr-filter-select">
            <label>Estado</label>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="">Todos</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {getStatusLabel(status)}
                </option>
              ))}
            </select>
          </div>

          <button type="button" className="hr-button hr-button--ghost" onClick={handleClearFilters}>
            Limpiar filtros
          </button>
        </div>
      </div>

      {loadingEmployeesList ? (
        <div>Cargando empleados...</div>
      ) : errorEmployeesText ? (
        <div className="hr-error-text">Error: {errorEmployeesText}</div>
      ) : employeesList.length === 0 ? (
        <div>No hay empleados registrados.</div>
      ) : (
        <div className="hr-manager-team-layout">
          {/* Lista lateral */}
          <div className="hr-manager-team-list">
            {filteredEmployees.length === 0 ? (
              <div className="hr-manager-team-empty">
                No se han encontrado empleados con estos filtros.
              </div>
            ) : (
              filteredEmployees.map((employee) => {
                const user = employee.user || {};
                const fullName =
                  ((user.first_name || "") + " " + (user.last_name || "")).trim() ||
                  user.username ||
                  "Sin nombre";

                const isActive = selectedEmployeeId === employee.id;

                const avatarUrl =
                  user.profile_picture_url ||
                  user.profile_picture ||
                  user.profilePicture ||
                  null;

                return (
                  <button
                    key={employee.id}
                    type="button"
                    className={
                      "hr-manager-team-item" + (isActive ? " hr-manager-team-item--active" : "")
                    }
                    onClick={() => setSelectedEmployeeId(employee.id)}
                  >
                    <div className="hr-manager-team-item-avatar">
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt={fullName}
                          className="hr-manager-team-item-avatar-img"
                        />
                      ) : (
                        <UserCircle2 className="hr-manager-team-item-icon" aria-hidden="true" />
                      )}
                    </div>

                    <div className="hr-manager-team-item-text">
                      <span className="hr-manager-team-item-name">{fullName}</span>
                      <span className="hr-manager-team-item-sub">
                        {employee.position || "Puesto no definido"} ·{" "}
                        {employee.department || "Sin departamento"}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Panel de detalle */}
          <div className="hr-manager-team-detail">
            {!selectedEmployee ? (
              <div className="hr-manager-team-empty">Selecciona un empleado para ver su ficha.</div>
            ) : (
              (() => {
                const user = selectedEmployee.user || {};
                const fullName =
                  ((user.first_name || "") + " " + (user.last_name || "")).trim() ||
                  user.username ||
                  "Sin nombre";

                const email = user.email || "-";

                const employmentTypeLabel = getEmploymentTypeLabel(selectedEmployee.employment_type);
                const statusLabel = getStatusLabel(selectedEmployee.status);

                const managerName = getManagerNameForEmployee(selectedEmployee);

                const avatarUrl =
                  user.profile_picture_url ||
                  user.profile_picture ||
                  user.profilePicture ||
                  null;

                return (
                  <div className="hr-manager-team-detail-card">
                    <div className="hr-manager-team-detail-header">
                      <div className="hr-manager-team-detail-main">
                        <div className="hr-manager-team-detail-avatar">
                          {avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt={fullName}
                              className="hr-manager-team-detail-avatar-img"
                            />
                          ) : (
                            <UserCircle2
                              className="hr-manager-team-detail-avatar-icon"
                              aria-hidden="true"
                            />
                          )}
                        </div>

                        <div className="hr-manager-team-detail-headtext">
                          <h3 className="hr-manager-team-detail-name">{fullName}</h3>

                          {isEditing ? (
                            <>
                              <input
                                type="text"
                                className="hr-info-input"
                                placeholder="Puesto"
                                value={editForm.position}
                                onChange={(e) => handleEditFieldChange("position", e.target.value)}
                              />
                              <input
                                type="text"
                                className="hr-info-input"
                                placeholder="Departamento"
                                value={editForm.department}
                                onChange={(e) =>
                                  handleEditFieldChange("department", e.target.value)
                                }
                              />
                            </>
                          ) : (
                            <>
                              <p className="hr-manager-team-detail-position">
                                {selectedEmployee.position || "Puesto no definido"}
                              </p>
                              <p className="hr-manager-team-detail-department">
                                {selectedEmployee.department || "Sin departamento"}
                              </p>
                            </>
                          )}
                        </div>
                      </div>

                      <div
                        className={
                          "hr-manager-team-detail-actions" +
                          (isEditing ? " hr-manager-team-detail-actions--editing" : "")
                        }
                      >
                        <div className="hr-manager-team-detail-tags">
                          {isEditing ? (
                            <>
                              <div className="hr-filter-select">
                                <label>Estado</label>
                                <select
                                  value={editForm.status}
                                  onChange={(e) => handleEditFieldChange("status", e.target.value)}
                                >
                                  <option value="active">Activo</option>
                                  <option value="on_leave">En baja / permiso</option>
                                  <option value="inactive">Inactivo</option>
                                </select>
                              </div>

                              <div className="hr-filter-select">
                                <label>Tipo de contrato</label>
                                <select
                                  value={editForm.employment_type}
                                  onChange={(e) =>
                                    handleEditFieldChange("employment_type", e.target.value)
                                  }
                                >
                                  <option value="">Seleccionar...</option>
                                  <option value="full_time">Jornada completa</option>
                                  <option value="part_time">Media jornada</option>
                                  <option value="contractor">Colaborador externo</option>
                                  <option value="intern">Prácticas</option>
                                </select>
                              </div>
                            </>
                          ) : (
                            <>
                              <span className="hr-pill hr-pill--status">{statusLabel}</span>
                              <span className="hr-pill hr-pill--type">{employmentTypeLabel}</span>
                            </>
                          )}
                        </div>

                        {!isEditing && (
                          <div className="hr-manager-team-action-buttons">
                            <button
                              type="button"
                              className="hr-icon-button"
                              onClick={handleStartEditing}
                              aria-label="Editar ficha"
                              title="Editar ficha"
                            >
                              <Pencil width={16} height={16} aria-hidden="true" />
                            </button>

                            <button
                              type="button"
                              className="hr-icon-button hr-icon-button--danger"
                              onClick={openDeleteModal}
                              aria-label="Eliminar empleado"
                              title="Eliminar empleado"
                            >
                              <Trash2 width={16} height={16} aria-hidden="true" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="hr-manager-team-detail-grid">
                      <div className="hr-info-card">
                        <h4 className="hr-info-title">
                          <Briefcase className="hr-info-icon" aria-hidden="true" />
                          Información laboral
                        </h4>

                        <div className="hr-info-grid">
                          <div className="hr-info-row">
                            <span className="hr-info-label">Fecha de alta</span>
                            {isEditing ? (
                              <input
                                type="date"
                                className="hr-info-input"
                                value={editForm.start_date || ""}
                                onChange={(e) =>
                                  handleEditFieldChange("start_date", e.target.value)
                                }
                              />
                            ) : (
                              <span className="hr-info-value">
                                {selectedEmployee.start_date || "—"}
                              </span>
                            )}
                          </div>

                          <div className="hr-info-row">
                            <span className="hr-info-label">Fecha de baja</span>
                            {isEditing ? (
                              <input
                                type="date"
                                className="hr-info-input"
                                value={editForm.end_date || ""}
                                onChange={(e) => handleEditFieldChange("end_date", e.target.value)}
                              />
                            ) : (
                              <span className="hr-info-value">
                                {selectedEmployee.end_date || "—"}
                              </span>
                            )}
                          </div>

                          <div className="hr-info-row">
                            <span className="hr-info-label">Horas semanales</span>
                            {isEditing ? (
                              <input
                                type="number"
                                min="0"
                                className="hr-info-input"
                                value={editForm.weekly_hours}
                                onChange={(e) =>
                                  handleEditFieldChange("weekly_hours", e.target.value)
                                }
                              />
                            ) : (
                              <span className="hr-info-value">
                                {selectedEmployee.weekly_hours ?? 0} h
                              </span>
                            )}
                          </div>

                          <div className="hr-info-row">
                            <span className="hr-info-label">Vacaciones anuales</span>
                            {isEditing ? (
                              <input
                                type="number"
                                min="0"
                                className="hr-info-input"
                                value={editForm.annual_vacation_days}
                                onChange={(e) =>
                                  handleEditFieldChange("annual_vacation_days", e.target.value)
                                }
                              />
                            ) : (
                              <span className="hr-info-value">
                                {selectedEmployee.annual_vacation_days ?? 0} días
                              </span>
                            )}
                          </div>

                          <div className="hr-info-row">
                            <span className="hr-info-label">Responsable</span>
                            <span className="hr-info-value">{managerName || "Sin asignar"}</span>
                          </div>
                        </div>
                      </div>

                      <div className="hr-info-card">
                        <h4 className="hr-info-title">
                          <UserCircle2 className="hr-info-icon" aria-hidden="true" />
                          Datos de contacto
                        </h4>

                        <div className="hr-info-grid">
                          <div className="hr-info-row">
                            <span className="hr-info-label">Email</span>
                            <span className="hr-info-value">{email}</span>
                          </div>
                          <div className="hr-info-row">
                            <span className="hr-info-label">Usuario</span>
                            <span className="hr-info-value">{user.username || "—"}</span>
                          </div>
                          <div className="hr-info-row">
                            <span className="hr-info-label">Rol</span>
                            <span className="hr-info-value">{getRoleLabel(user.role)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {isEditing && (
                      <div className="hr-manager-team-detail-footer">
                        <button
                          type="button"
                          className="hr-button hr-button--primary"
                          onClick={handleSave}
                          disabled={loadingUpdateEmployeeProfile}
                        >
                          {loadingUpdateEmployeeProfile ? "Guardando..." : "Guardar cambios"}
                        </button>

                        <button
                          type="button"
                          className="hr-button hr-button--ghost"
                          onClick={handleCancelEdit}
                          disabled={loadingUpdateEmployeeProfile}
                        >
                          Cancelar
                        </button>
                      </div>
                    )}

                    {errorUpdateText && (
                      <div className="hr-error-text">Error al actualizar empleado: {errorUpdateText}</div>
                    )}

                    {saveSuccessMessage && <div className="hr-success-text">{saveSuccessMessage}</div>}
                  </div>
                );
              })()
            )}
          </div>
        </div>
      )}

      {/* MODAL CREAR EMPLEADO */}
      {isCreateModalOpen && (
        <div
          className="hr-modal-overlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeCreateModal();
          }}
        >
          <div className="hr-modal">
            <div className="hr-modal-header">
              <div>
                <h3 className="hr-modal-title">Crear empleado</h3>
                <p className="hr-modal-subtitle">
                  El usuario se genera automáticamente igual que el email.
                </p>
              </div>

              <button
                type="button"
                className="hr-modal-close"
                onClick={closeCreateModal}
                aria-label="Cerrar"
              >
                <X aria-hidden="true" />
              </button>
            </div>

            <div className="hr-modal-body">
              <div className="hr-modal-grid">
                <div className="hr-modal-section">
                  <h4 className="hr-modal-section-title">Datos personales</h4>

                  <div className="hr-form-row">
                    <label className="hr-form-label">Email *</label>
                    <input
                      className="hr-info-input"
                      type="email"
                      placeholder="empleado@clinica.com"
                      value={createForm.email}
                      onChange={(e) => handleCreateFieldChange("email", e.target.value)}
                    />
                  </div>

                  <div className="hr-form-row">
                    <label className="hr-form-label">Nombre</label>
                    <input
                      className="hr-info-input"
                      type="text"
                      placeholder="Nombre"
                      value={createForm.firstName}
                      onChange={(e) => handleCreateFieldChange("firstName", e.target.value)}
                    />
                  </div>

                  <div className="hr-form-row">
                    <label className="hr-form-label">Apellidos</label>
                    <input
                      className="hr-info-input"
                      type="text"
                      placeholder="Apellidos"
                      value={createForm.lastName}
                      onChange={(e) => handleCreateFieldChange("lastName", e.target.value)}
                    />
                  </div>

                  <div className="hr-form-row">
                    <label className="hr-form-label">Teléfono</label>
                    <input
                      className="hr-info-input"
                      type="text"
                      placeholder="+34..."
                      value={createForm.phone}
                      onChange={(e) => handleCreateFieldChange("phone", e.target.value)}
                    />
                  </div>

                  <div className="hr-form-row">
                    <label className="hr-form-label">Rol *</label>
                    <select
                      className="hr-info-input"
                      value={createForm.role}
                      onChange={(e) => handleCreateFieldChange("role", e.target.value)}
                    >
                      <option value="receptionist">Recepcionista</option>
                      <option value="doctor">Médico</option>
                      <option value="admin">Administrador</option>
                    </select>
                  </div>

                  <div className="hr-form-row">
                    <label className="hr-form-label">Contraseña (opcional)</label>
                    <input
                      className="hr-info-input"
                      type="password"
                      placeholder="Dejar vacío = sin acceso directo"
                      value={createForm.password}
                      onChange={(e) => handleCreateFieldChange("password", e.target.value)}
                    />
                  </div>
                </div>

                <div className="hr-modal-section">
                  <h4 className="hr-modal-section-title">Información laboral</h4>

                  <div className="hr-form-row">
                    <label className="hr-form-label">Puesto</label>
                    <input
                      className="hr-info-input"
                      type="text"
                      placeholder="Puesto"
                      value={createForm.position}
                      onChange={(e) => handleCreateFieldChange("position", e.target.value)}
                    />
                  </div>

                  <div className="hr-form-row">
                    <label className="hr-form-label">Departamento</label>
                    <input
                      className="hr-info-input"
                      type="text"
                      placeholder="Departamento"
                      value={createForm.department}
                      onChange={(e) => handleCreateFieldChange("department", e.target.value)}
                    />
                  </div>

                  <div className="hr-form-row">
                    <label className="hr-form-label">Tipo de contrato</label>
                    <select
                      className="hr-info-input"
                      value={createForm.employmentType}
                      onChange={(e) => handleCreateFieldChange("employmentType", e.target.value)}
                    >
                      <option value="full_time">Jornada completa</option>
                      <option value="part_time">Media jornada</option>
                      <option value="contractor">Colaborador externo</option>
                      <option value="intern">Prácticas</option>
                    </select>
                  </div>

                  <div className="hr-form-row">
                    <label className="hr-form-label">Estado</label>
                    <select
                      className="hr-info-input"
                      value={createForm.status}
                      onChange={(e) => handleCreateFieldChange("status", e.target.value)}
                    >
                      <option value="active">Activo</option>
                      <option value="on_leave">En baja / permiso</option>
                      <option value="inactive">Inactivo</option>
                    </select>
                  </div>

                  <div className="hr-form-row">
                    <label className="hr-form-label">Horas semanales</label>
                    <input
                      className="hr-info-input"
                      type="number"
                      min="0"
                      value={createForm.weeklyHours}
                      onChange={(e) => handleCreateFieldChange("weeklyHours", e.target.value)}
                    />
                  </div>

                  <div className="hr-form-row">
                    <label className="hr-form-label">Vacaciones anuales</label>
                    <input
                      className="hr-info-input"
                      type="number"
                      min="0"
                      value={createForm.annualVacationDays}
                      onChange={(e) =>
                        handleCreateFieldChange("annualVacationDays", e.target.value)
                      }
                    />
                  </div>

                  <div className="hr-form-row">
                    <label className="hr-form-label">Fecha de alta</label>
                    <input
                      className="hr-info-input"
                      type="date"
                      value={createForm.startDate}
                      onChange={(e) => handleCreateFieldChange("startDate", e.target.value)}
                    />
                  </div>

                  <div className="hr-form-row">
                    <label className="hr-form-label">Fecha de baja</label>
                    <input
                      className="hr-info-input"
                      type="date"
                      value={createForm.endDate}
                      onChange={(e) => handleCreateFieldChange("endDate", e.target.value)}
                    />
                  </div>

                  <div className="hr-form-row">
                    <label className="hr-form-label">Responsable</label>
                    <select
                      className="hr-info-input"
                      value={createForm.managerId}
                      onChange={(e) => handleCreateFieldChange("managerId", e.target.value)}
                    >
                      <option value="">Sin asignar</option>
                      {managerOptions.map((option) => (
                        <option key={option.userId} value={String(option.userId)}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {(createLocalError || errorCreateText) && (
                <div className="hr-error-text" style={{ marginTop: 12 }}>
                  {createLocalError || errorCreateText}
                </div>
              )}
            </div>

            <div className="hr-modal-footer">
              <button
                type="button"
                className="hr-button hr-button--ghost"
                onClick={closeCreateModal}
                disabled={loadingCreateEmployee}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="hr-button hr-button--primary"
                onClick={handleCreateEmployee}
                disabled={loadingCreateEmployee}
              >
                {loadingCreateEmployee ? "Creando..." : "Crear empleado"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ELIMINAR (global ui modal, sin X) */}
      {isDeleteModalOpen && (
        <div className="ui-modal-overlay" onClick={closeDeleteModal} role="presentation">
          <div
            className="ui-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ui-delete-employee-title"
            aria-describedby="ui-delete-employee-desc"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ui-modal-header">
              <h3 id="ui-delete-employee-title">Eliminar empleado</h3>
            </div>

            <div className="ui-modal-body ui-modal-body--single">
              <div className="ui-delete-help" id="ui-delete-employee-desc">
                ¿Seguro que quieres eliminar <strong>{selectedEmployeeNameForDelete}</strong>?
                <div className="ui-delete-subhelp">
                  Esta acción desactiva el acceso del usuario y marca el empleado como inactivo.
                </div>
              </div>

              {errorDeleteText ? (
                <div className="ui-modal-error" role="alert" aria-live="polite">
                  {errorDeleteText}
                </div>
              ) : null}
            </div>

            <div className="ui-modal-footer">
              <button
                type="button"
                className="ghost btn-sm"
                onClick={closeDeleteModal}
                disabled={loadingDeleteEmployeeProfile}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="btn-danger btn-sm"
                onClick={handleConfirmDelete}
                disabled={loadingDeleteEmployeeProfile}
              >
                {loadingDeleteEmployeeProfile ? "Eliminando..." : "Eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteSuccessMessage && <div className="hr-success-text">{deleteSuccessMessage}</div>}
    </div>
  );
};

export default HrManagerEmployeesTab;